"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/observability/report-error";

/**
 * Global client-error telemetry. Mounts `window` `error` +
 * `unhandledrejection` listeners and forwards them to `/api/client-error`.
 * Throttled + deduped so a tight error/reload loop (the failure mode Clarity
 * caught on /angebot) can't spam the logs. Renders nothing.
 */
export function ClientErrorReporter() {
  useEffect(() => {
    const seen = new Set<string>();
    let count = 0;
    const CAP = 10; // distinct reports per page view

    function handle(
      message: string,
      error: unknown,
      context: Record<string, unknown>,
    ) {
      if (count >= CAP) return;
      const key = message.slice(0, 200);
      if (seen.has(key)) return;
      seen.add(key);
      count += 1;
      reportClientError(error ?? new Error(message), context);
    }

    function onError(e: ErrorEvent) {
      handle(e.message || "window.error", e.error, {
        boundary: "window.onerror",
        source: e.filename,
        line: e.lineno,
      });
    }
    function onRejection(e: PromiseRejectionEvent) {
      const reason = e.reason;
      const msg =
        reason instanceof Error
          ? reason.message
          : String(reason ?? "unhandledrejection");
      handle(msg, reason, { boundary: "unhandledrejection" });
    }

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
